/**
 * Live lists that never move under the reader (DesignSystem 3.4 and 6.3). While the list
 * is held (scrolled down, pointed at, or with a selection) rows keep their places and new
 * rows wait behind a "3 new, show" bar; once released, the list takes the latest order.
 * Rows whose content changed get a short highlight in place.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface Reconciled {
  /** Keys in the order to render. */
  shown: string[];
  /** New keys held back while the list is held, in their latest order. */
  pending: string[];
}

/**
 * Next display order. Released: exactly `next`. Held: rows already shown keep their order
 * (rows that left drop out), rows not yet shown wait in `pending`.
 */
export function reconcileOrder(shown: readonly string[], next: readonly string[], held: boolean): Reconciled {
  // Nothing on screen to hold: show the latest.
  if (!held || !shown.length) return { shown: [...next], pending: [] };
  const inNext = new Set(next);
  const kept = shown.filter((k) => inNext.has(k));
  const keptSet = new Set(kept);
  return { shown: kept, pending: next.filter((k) => !keptSet.has(k)) };
}

/** Keys whose version differs from the last one seen (new keys are not "changed"). */
export function changedKeys(prev: ReadonlyMap<string, unknown>, next: ReadonlyMap<string, unknown>): string[] {
  const out: string[] = [];
  for (const [k, v] of next) if (prev.has(k) && prev.get(k) !== v) out.push(k);
  return out;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export interface LiveOrder {
  order: string[];
  /** How many new rows wait behind the bar. */
  pending: number;
  /** Show the held rows now (the bar's "show" button). */
  release: () => void;
  /** Keys highlighted because they just changed. */
  flashing: ReadonlySet<string>;
}

/**
 * Hold the order of `keys` while `held` is true. `versionOf` (optional, keep it stable)
 * returns something that changes when a row's content changes; those rows flash for `flashMs`.
 */
export function useLiveOrder(
  keys: readonly string[],
  held: boolean,
  opts: { versionOf?: (key: string) => unknown; flashMs?: number; enabled?: boolean } = {},
): LiveOrder {
  const { versionOf, flashMs = 600, enabled = true } = opts;
  const [state, setState] = useState<Reconciled>(() => ({ shown: [...keys], pending: [] }));
  const [forced, setForced] = useState(0);
  const lastForced = useRef(0);

  useEffect(() => {
    // Decide outside the updater: updaters must stay pure (StrictMode calls them twice).
    const force = forced !== lastForced.current;
    lastForced.current = forced;
    const hold = enabled && held && !force;
    setState((s) => {
      const r = reconcileOrder(s.shown, keys, hold);
      return sameList(r.shown, s.shown) && sameList(r.pending, s.pending) ? s : r;
    });
  }, [keys, held, enabled, forced]);

  const release = useCallback(() => setForced((n) => n + 1), []);

  const versions = useMemo(() => {
    const m = new Map<string, unknown>();
    if (versionOf) for (const k of keys) m.set(k, versionOf(k));
    return m;
  }, [keys, versionOf]);
  const prevVersions = useRef<Map<string, unknown>>(versions);
  const [flashing, setFlashing] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    const changed = changedKeys(prevVersions.current, versions);
    prevVersions.current = versions;
    if (!changed.length) return;
    setFlashing((f) => new Set([...f, ...changed]));
    const t = setTimeout(
      () =>
        setFlashing((f) => {
          const n = new Set(f);
          for (const k of changed) n.delete(k);
          return n;
        }),
      flashMs,
    );
    return () => clearTimeout(t);
  }, [versions, flashMs]);

  return { order: state.shown, pending: state.pending.length, release, flashing };
}
